import type { RequestsFormValues } from '../Requests/models';

export type ApiComplementType =
  | 'ct' | 'rtg' | 'toxicology' | 'tryptase' | 'microbiology' | 'genetics'
  | 'biochemistry' | 'histology' | 'nrl_other' | 'nrl_respiratory' | 'transport' | 'virology';

export const API_COMPLEMENT_TYPES = [
  'ct', 'rtg', 'toxicology', 'tryptase', 'microbiology', 'genetics',
  'biochemistry', 'histology', 'nrl_other', 'nrl_respiratory', 'transport', 'virology'
] as const;

export const API_KEY_MAP: Record<ApiComplementType, string> = {
  ct: 'ct', rtg: 'rtg', toxicology: 'toxicology', tryptase: 'tryptase',
  microbiology: 'microbiology', genetics: 'genetics', biochemistry: 'biochemistry',
  histology: 'histology', nrl_other: 'nrlOther', nrl_respiratory: 'nrlRespiratory',
  transport: 'transport', virology: 'virology'
};

export const TYPE_OPTIONS: { value: ApiComplementType; label: string }[] = [
  { value: 'toxicology', label: 'Toxikologie' },
  { value: 'rtg', label: 'RTG' },
  { value: 'ct', label: 'CT' },
  { value: 'tryptase', label: 'Tryptáza' },
  { value: 'microbiology', label: 'Mikrobiologie' },
  { value: 'histology', label: 'Histologie' },
  { value: 'nrl_respiratory', label: 'NRL respirační' },
  { value: 'nrl_other', label: 'NRL jiné' },
  { value: 'genetics', label: 'Genetika' },
  { value: 'biochemistry', label: 'Biochemie' },
  { value: 'transport', label: 'Transport' },
  { value: 'virology', label: 'Virologie' }
];

export function openPdfBlob(response: { status: number; data: Blob }): void {
  if (response.status === 200) {
    const blob = new Blob([response.data], { type: 'application/pdf' });
    const url = window.URL.createObjectURL(blob);
    window.open(url);
  }
}

export function mapDataToFormValues(
  type: ApiComplementType,
  data: any
): Partial<RequestsFormValues> {
  const base: Partial<RequestsFormValues> = {
    selectedComplement: type as any,
    examinationType: String(data.examination_type ?? ''),
    fnbToxicologist: String(data.toxicologist ?? '')
  };
  switch (type) {
    case 'ct':
      return { ...base, ct: { request_text: data.request_text ?? null } };
    case 'rtg':
      return { ...base, rtg: { request_text: data.request_text ?? null } };
    case 'toxicology':
      return { ...base, toxicology: {
        intoxication_suspicion: data.intoxication_suspicion ?? 'NE',
        circumstances_note: data.circumstances_note ?? '',
        note: data.note ?? '',
        form_data: data.form_data
      }};
    case 'tryptase':
      return { ...base, tryptase: { do_request: data.do_request ?? false } };
    case 'microbiology':
      return { ...base, microbiology: { note: data.note ?? '', form_data: data.form_data } };
    case 'genetics':
      return { ...base, genetics: { pcr: data.pcr ?? null, medical: data.medical ?? null } };
    case 'biochemistry':
      return { ...base, biochemistry: { primary_sample: data.primary_sample ?? null, form_data: data.form_data } };
    case 'histology':
      return { ...base, histology: {
        requesting_physician: data.requesting_physician ? String(data.requesting_physician) : '',
        macroscopic_heart_histochemistry: data.macroscopic_heart_histochemistry ?? false,
        form_data: data.form_data
      }};
    case 'nrl_other':
      return { ...base, nrl_other: {
        swab_nose_throat_collected_at: data.swab_nose_throat_collected_at ?? null,
        swab_nose_throat_note: data.swab_nose_throat_note ?? '',
        endotracheal_swab_aspirate_sputum_collected_at: data.endotracheal_swab_aspirate_sputum_collected_at ?? null,
        endotracheal_swab_aspirate_sputum_note: data.endotracheal_swab_aspirate_sputum_note ?? '',
        bal_collected_at: data.bal_collected_at ?? null,
        bal_note: data.bal_note ?? '',
        csf_collected_at: data.csf_collected_at ?? null,
        csf_note: data.csf_note ?? '',
        biopsy_collected_at: data.biopsy_collected_at ?? null,
        biopsy_note: data.biopsy_note ?? '',
        full_blood_clot_collected_at: data.full_blood_clot_collected_at ?? null,
        full_blood_clot_note: data.full_blood_clot_note ?? '',
        saliva_collected_at: data.saliva_collected_at ?? null,
        saliva_note: data.saliva_note ?? '',
        autopsy_swabs_collected_at: data.autopsy_swabs_collected_at ?? null,
        autopsy_swabs_note: data.autopsy_swabs_note ?? '',
        other_material_name: data.other_material_name ?? '',
        other_collected_at: data.other_collected_at ?? null,
        other_note: data.other_note ?? ''
      }};
    case 'nrl_respiratory':
      return { ...base, nrl_respiratory: {
        important_symptoms_note: data.important_symptoms_note ?? '',
        focus_note: data.focus_note ?? '',
        form_data: data.form_data
      }};
    case 'transport':
      return { ...base, transport: {
        requesting_department: data.requesting_department ? String(data.requesting_department) : '',
        form_data: data.form_data
      }};
    case 'virology':
      return { ...base, virology: { form_data: data.form_data } };
    default:
      return base;
  }
}
